<template>
  <div id="general_tab" class="my-6">
    <NotificationPopup
      v-show="widget.edited"
      status="warning"
      title="Changes Unpublished"
      text="To view your updates on the widget, please save and publish them."
      class="mb-6"
    />

    <div class="tab-wrapper-primary mb-6">
      <div class="tab-heading-primary">{{ changeCase('widget name & description', ElementE.TITLE) }}</div>
      <div class="flex flex-col gap-4 p-6">
        <div class="w-full pt-2">
          <div class="relative">
            <label class="label-primary" for="">
              {{ changeCase('widget name', ElementE.LABEL) }}
            </label>
            <input
              v-on:input="saveWidgetName(widget.name as string)"
              v-model.trim="widget.name"
              class="input-primary"
              :class="errors.widgetName && 'border-critical'"
              type="text"
              :placeholder="(changeCase('enter name', ElementE.PLACEHOLDER) as string)"
            />
            <div v-if="errors.widgetName">
              <div class="absolute top-3.5 right-3">
                <ErrorIcon />
              </div>
              <p class="mt-1 text-xs text-critical-main">
                {{ errors.widgetName }}
              </p>
            </div>
          </div>
        </div>

        <div class="w-full pt-2">
          <div class="relative">
            <label class="label-primary" for="">
              {{ changeCase('widget description', ElementE.LABEL) }}
            </label>
            <textarea
              v-on:input="saveWidgetDescription(widget.description as string)"
              v-model.trim="widget.description"
              class="input-primary block"
              :class="
                errors.widgetDescription && 'border-critical'
              "
              :placeholder="(changeCase('enter description', ElementE.PLACEHOLDER) as string)"
            />
            <div v-if="errors.widgetDescription">
              <div class="absolute top-3.5 right-3">
                <ErrorIcon />
              </div>
              <p class="mt-1 text-xs text-critical-main">
                {{ errors.widgetDescription }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Specials -->
    <div class="tab-wrapper-primary mb-6 transition-all ease-in-out duration-300">
      <p class="tab-heading-primary">Specials</p>
      <div class="relative flex flex-col gap-4 p-6">
        <CheckboxSlide
          :key="apiCallExist.specials.toString()"
          :isChecked="apiCallExist.specials"
          :disabled="apiCallExist.specials || isSpecialsLoading"
          :tooltipTxt="apiCallExist.specials ? 'Specials already activated' : 'Specials not activated yet'"
          label="Activate specials"
          @update:isChecked="activateSpecials"
        />
        <div
          v-show="apiCallExist.specials && !isSpecialsLoading"
          class="max-w-[300px] pt-2"
        >
          <div>
            <CheckboxSlide
              :isChecked="specials.enabled"
              :disabled="!apiCallExist?.specials"
              label="Enable specials"
              class="pb-2"
              @update:isChecked="enableSpecials($event, 'enabled')"
            />
            <Transition>
              <div
                v-show="specials.enabled"
                class="pl-[46px]"
              >
                <span class="block mb-2 text-sm font-normal text-secondary">Matrix</span>
                <div>
                  <CheckboxSlide
                    label="Above matrix date selection"
                    :disabled="!apiCallExist?.specials"
                    :isChecked="specials.above_matrix"
                    @update:isChecked="enableSpecials($event, 'above_matrix')"
                  />
                </div>
                <div class="my-2 border-t-[1px] border-dashed"></div>
                <span class="block mb-2 text-sm font-normal text-secondary">Search, Search & Matrix</span>
                <div class="mb-2">
                  <CheckboxSlide
                    label="Near search bar"
                    :disabled="!apiCallExist?.specials"
                    :isChecked="specials.near_search"
                    @update:isChecked="enableSpecials($event, 'near_search')"
                  />
                </div>
                <div class="mb-4">
                  <CheckboxSlide
                    label="Between search results"
                    :disabled="!apiCallExist?.specials"
                    :isChecked="specials.between_available"
                    @update:isChecked="enableSpecials($event, 'between_available')"
                  />
                </div>
              </div>
            </Transition>
          </div>
          <div>
            <CheckboxSlide
              label="Enable 'Only 1 left'"
              :isChecked="specials.only_one_left"
              @update:isChecked="enableSpecials($event, 'only_one_left')"
            />
          </div>
        </div>

        <div v-show="isSpecialsNotValid" class="max-w-[700px]">
          <p v-html="specialsErrorMessage" class="text-sm" />
        </div>

        <OverlaySpinner v-if="isSpecialsLoading" />
      </div>
    </div>

    <!--Bookings & Payments-->
    <div ref="bookingAndPayments" class="tab-wrapper-primary mt-6 transition-all">
      <div class="tab-heading-primary">{{ changeCase('bookings & payments', ElementE.TITLE) }}</div>
      <div class="relative flex flex-col gap-4 p-6">

         <!--E-mail or url for 'contact us-->
        <div class="w-full pt-2">
          <div class="relative">
            <label
              for="contact-email"
              class="label-primary"
            >
              E-mail or URL for "Contact us"
            </label>
            <input
              v-model.trim="contactEmailFieldValue"
              id="contact-email"
              type="text"
              class="input-primary"
              :class="errors.contactEmail && 'border-critical'"
              :placeholder="(changeCase('enter e-mail or url', ElementE.PLACEHOLDER) as string)"
              @change="saveContactEmail(contactEmailFieldValue)"
              @blur="validateEmail('contactEmail', contactEmailFieldValue)"
            />
            <div v-if="errors.contactEmail">
              <div class="absolute top-3.5 right-3">
                <ErrorIcon />
              </div>
              <p class="mt-1 text-xs text-critical-main">{{ errors.contactEmail }}</p>
            </div>
          </div>
        </div>

        <!--Enable bookings-->
        <div class="flex items-center gap-1">
          <CheckboxSlide
            @click="bookingEnable"
            label="Enable bookings"
            info="some info"
            :isChecked="isBookingChecked"
          />

          <BaseTooltip
            tooltipText="For ResNova bookings to be confirmed successfuly in ResRequest, the ResRequest User Access Permissions for the ResNova user must allow Status Changes: 'Non-stock holding to Stock holding'"
          >
            <template #icon>
              <QuestionMarkIcon />
            </template>
          </BaseTooltip>
        </div>

        <Transition name="fade">
          <div v-show="isBookingChecked">

          <!--Copy of a booking E-mail-->
          <div class="w-full py-2">
            <div class="relative">
              <label
                for="cc-email"
                class="label-primary"
              >
                {{ changeCase('send a copy of a booking e-mail to...', ElementE.LABEL) }}
              </label>
              <input
                v-model.trim="ccEmailsFieldValue"
                id="cc-email"
                type="text"
                class="input-primary"
                :class="
                  errors.ccEmails && 'border-critical'
                "
                :placeholder="(changeCase('enter e-mail', ElementE.PLACEHOLDER) as string)"
                @change="saveCcEmails(ccEmailsFieldValue)"
                @blur="
                  validateEmail('ccEmails', ccEmailsFieldValue)
                "
              />
              <div v-if="errors.ccEmails">
                <div class="absolute top-3.5 right-3">
                  <ErrorIcon />
                </div>
                <p class="mt-1 text-xs text-critical-main">
                  {{ errors.ccEmails }}
                </p>
              </div>
            </div>
          </div>

          <div class="flex gap-4">

            <!--E-mail originates name-->
            <div class="w-full pt-2">
              <div class="relative">
                <label class="label-primary" for="person-name">
                  {{ changeCase('name', ElementE.LABEL) }}
                </label>
                <input
                  v-model.trim="personNameFieldValue"
                  id="person-name"
                  type="text"
                  class="input-primary"
                  :class="errors.personName && 'border-critical'"
                  :placeholder="(changeCase('name from which the e-mail originates', ElementE.PLACEHOLDER) as string)"
                  @change="saveName(personNameFieldValue)"
                  @blur="validateField('personName', personNameFieldValue)"
                />
                <div v-if="errors.personName">
                  <div class="absolute top-3.5 right-3">
                    <ErrorIcon />
                  </div>
                  <p class="mt-1 text-xs text-critical-main">
                    {{ errors.personName }}
                  </p>
                </div>
              </div>
            </div>


            <!--E-mail originates-->
            <div class="w-full pt-2">
              <div class="relative">
                <label
                  for="originating-email"
                  class="label-primary"
                >
                  {{ changeCase('e-mail', ElementE.LABEL) }}
                </label>
                <input
                  v-model.trim="originatingEmailFieldValue"
                  id="originating-email"
                  type="text"
                  class="input-primary"
                  :class="
                    errors.originatingEmail && 'border-critical'
                  "
                  :placeholder="(changeCase('the originating e-mail address', ElementE.PLACEHOLDER) as string)"
                  @change="saveOriginatingEmail(originatingEmailFieldValue)"
                  @blur="
                    validateEmail('originatingEmail', originatingEmailFieldValue)
                  "
                />
                <div v-if="errors.originatingEmail">
                  <div class="absolute top-3.5 right-3">
                    <ErrorIcon />
                  </div>
                  <p class="mt-1 text-xs text-critical-main">
                    {{ errors.originatingEmail }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Additional Email Links Section -->
          <!-- Section Heading Input -->
          <div class="w-full pt-4 mb-4">
            <div class="border border-dashed border-neutral-50" />
            <div class="relative mt-4">
              <label
                for="additional-links-heading"
                class="label-primary"
              >
                {{ changeCase('additional email links heading', ElementE.LABEL) }}
              </label>
              <input
                id="additional-links-heading"
                v-model.trim="additionalEmailLinksHeading"
                type="text"
                class="input-primary"
                :class="errors.additionalEmailLinksHeading && 'border-critical'"
                :placeholder="(changeCase('additional email links heading', ElementE.PLACEHOLDER) as string)"
                @change="saveAdditionalEmailLinksHeading(additionalEmailLinksHeading)"
                @blur="validateField('additionalEmailLinksHeading', additionalEmailLinksHeading)"
              />
              <div v-if="errors.additionalEmailLinksHeading">
                <div class="absolute top-3.5 right-3">
                  <ErrorIcon />
                </div>
                <p class="mt-1 text-xs text-critical-main">
                  {{ errors.additionalEmailLinksHeading }}
                </p>
              </div>
            </div>
          </div>
          <!-- Layout Switcher -->
          <div class="flex flex-col gap-2 mb-2">
            <h4 class="text-base font-medium text-neutral-125">
              {{ changeCase('Additional links layout', ElementE.TITLE) }}
            </h4>
            <div class="flex items-center gap-2">
              <BaseTooltip
                tooltipText="List layout - Links will appear vertically stacked in the confirmation email"
              >
                <template #icon>
                  <button
                    type="button"
                    :class="['px-2 py-1 rounded border', additionalEmailLinksLayout === 'list' ? 'bg-primary-main text-white border-primary-main' : 'bg-white text-primary-main border-primary-main']"
                    @click="setAdditionalEmailLinksLayout('list')"
                    :aria-pressed="additionalEmailLinksLayout === 'list'"
                  >
                    <!-- List icon -->
                    <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                      <rect x="4" y="5" width="12" height="2" rx="1" fill="currentColor"/>
                      <rect x="4" y="9" width="12" height="2" rx="1" fill="currentColor"/>
                      <rect x="4" y="13" width="12" height="2" rx="1" fill="currentColor"/>
                    </svg>
                  </button>
                </template>
              </BaseTooltip>
              <BaseTooltip
                tooltipText="Inline layout - Links will appear horizontally side by side in the confirmation email"
              >
                <template #icon>
                  <button
                    type="button"
                    :class="['px-2 py-1 rounded border', additionalEmailLinksLayout === 'inline' ? 'bg-primary-main text-white border-primary-main' : 'bg-white text-primary-main border-primary-main']"
                    @click="setAdditionalEmailLinksLayout('inline')"
                    :aria-pressed="additionalEmailLinksLayout === 'inline'"
                  >
                    <!-- Inline icon -->
                    <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                      <rect x="5" y="8" width="2" height="4" rx="1" fill="currentColor"/>
                      <rect x="9" y="8" width="2" height="4" rx="1" fill="currentColor"/>
                      <rect x="13" y="8" width="2" height="4" rx="1" fill="currentColor"/>
                    </svg>
                  </button>
                </template>
              </BaseTooltip>
            </div>
          </div>
          <!-- End Layout Switcher -->

          <div class="flex items-center justify-between mb-4">
            <h4 class="text-base font-medium text-neutral-125">
              {{ changeCase('links', ElementE.TITLE) }}
            </h4>
            <button
              type="button"
              class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-main bg-white border border-primary-main rounded-md hover:bg-primary-main hover:text-white transition-all"
              @click="addEmailLink"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 3C8.55228 3 9 3.44772 9 4V7H12C12.5523 7 13 7.44772 13 8C13 8.55228 12.5523 9 12 9H9V12C9 12.5523 8.55228 13 8 13C7.44772 13 7 12.5523 7 12V9H4C3.44772 9 3 8.55228 3 8C3 7.44772 3.44772 7 4 7H7V4C7 3.44772 7.44772 3 8 3Z"/>
              </svg>
              {{ changeCase('add link', ElementE.BUTTON) }}
            </button>
          </div>

          <div v-if="additionalEmailLinks.length > 0" class="flex flex-col gap-4">
            <div
              v-for="(link, index) in additionalEmailLinks"
              :key="link.id"
              class="flex flex-col gap-x-4 gap-y-1 p-4 bg-gray-100 rounded-md"
            >
              <!-- Link Title Header -->
              <div class="flex items-center justify-between">
                <h5 class="text-sm font-medium text-neutral-125">
                  {{ changeCase(`link ${index + 1}`, ElementE.TITLE) }}
                </h5>
                <!-- Remove Button -->
                <button
                  type="button"
                  class="flex items-center justify-center w-5 h-5 text-gray-800 hover:bg-transparent hover:text-critical-main rounded-md transition-all"
                  @click="removeEmailLink(index)"
                  :title="changeCase('delete link', ElementE.BUTTON) as string"
                >
                  <div class="w-4 h-4">
                    <TrashCanIcon />
                  </div>
                </button>
              </div>

              <div class="flex gap-4">
                <!-- Title Input -->
                <div class="w-1/2 pt-2">
                  <div class="relative">
                    <label
                      :for="`email-link-title-${link.id}`"
                      class="label-primary"
                    >
                      {{ changeCase('title', ElementE.LABEL) }}
                    </label>
                    <input
                      :id="`email-link-title-${link.id}`"
                      v-model.trim="link.title"
                      type="text"
                      class="input-primary"
                      :class="link.errors?.title && 'border-critical'"
                      :placeholder="(changeCase('enter link title', ElementE.PLACEHOLDER) as string)"
                      @change="saveEmailLink(index, 'title', link.title)"
                      @blur="validateEmailLink(index, 'title', link.title)"
                    />
                    <div v-if="link.errors?.title" class="absolute top-3.5 right-3">
                      <ErrorIcon />
                    </div>
                  </div>
                  <p v-if="link.errors?.title" class="mt-1 text-xs text-critical-main">
                    {{ link.errors.title }}
                  </p>
                </div>

                <!-- Link Input -->
                <div class="w-1/2 pt-2">
                  <div class="relative">
                    <label
                      :for="`email-link-url-${link.id}`"
                      class="label-primary"
                    >
                      {{ changeCase('URL', ElementE.LABEL) }}
                    </label>
                    <input
                      :id="`email-link-url-${link.id}`"
                      v-model.trim="link.url"
                      type="text"
                      class="input-primary"
                      :class="link.errors?.url && 'border-critical'"
                      :placeholder="(changeCase('enter url', ElementE.PLACEHOLDER) as string)"
                      @change="saveEmailLink(index, 'url', link.url)"
                      @blur="validateEmailLink(index, 'url', link.url)"
                    />
                    <div v-if="link.errors?.url" class="absolute top-3.5 right-3">
                      <ErrorIcon />
                    </div>
                  </div>
                  <p v-if="link.errors?.url" class="mt-1 text-xs text-critical-main">
                    {{ link.errors.url }}
                  </p>
                </div>
              </div>

              <!-- Description field - only show for list layout -->
              <div v-if="additionalEmailLinksLayout === 'list'" class="w-full pt-2">
                <div class="relative">
                  <label
                    :for="`email-link-description-${link.id}`"
                    class="label-primary"
                  >
                    {{ changeCase('description', ElementE.LABEL) }}
                  </label>
                  <textarea
                    :id="`email-link-description-${link.id}`"
                    v-model.trim="link.description"
                    class="input-primary block"
                    :class="link.errors?.description && 'border-critical'"
                    :placeholder="(changeCase('enter link description', ElementE.PLACEHOLDER) as string)"
                    rows="2"
                    @change="saveEmailLink(index, 'description', link.description || '')"
                    @blur="validateEmailLink(index, 'description', link.description || '')"
                  />
                  <div v-if="link.errors?.description" class="absolute top-3.5 right-3">
                    <ErrorIcon />
                  </div>
                </div>
                <p v-if="link.errors?.description" class="mt-1 text-xs text-critical-main">
                  {{ link.errors.description }}
                </p>
              </div>
            </div>
          </div>
          <div v-else class="text-center py-8 text-neutral-100">
            <p class="text-sm">
              {{ changeCase('no additional email links added yet', ElementE.PLACEHOLDER) }}
            </p>
          </div>
        </div>
        </Transition>

        <Transition name="fade">
        <div v-show="isBookingChecked" class="flex flex-col gap-4">
          <div class="border border-dashed border-neutral-50" />

          <CheckboxSlide
            @click="paymentEnable"
            id="enablePayment"
            label="Enable card payments"
            :isChecked="isPaymentChecked"
          />

          <NotificationPopup
            v-if="isShowPaymentError"
            status="error"
            title="Payment Gateway Issue"
            :text="errorTextWithSupportLink"
          />

          <Transition name="fade">
          <div v-show="isPaymentChecked">
            <div>
              <PaymentDropdown
                @input="paymentSet"
                label="Payment gateway"
                defaultId="1"
                :defaultSelect="paymentMethod"
                :values="payments"
                tab="general"
              />
            </div>
            <div class="flex gap-4 pt-4" v-if="isSimplePaymentField">
              <PaymentTextInput
                v-for="(names, key, index) in widget.config
                  .payment_gateway_config"
                :dvalue="names"
                :value="names"
                :key="index"
                :id="key"
                @input="savePaymentField"
                :inp-value="names"
                :label="key"
                :placeholder="names"
              />
            </div>
            <div class="flex gap-4 pt-4" v-else>
              <PaymentTextInput
                v-for="pField in paymentFields"
                @input="savePaymentField"
                :dvalue="getPaymentValue"
                :key="pField.id"
                :id="pField.field_name"
                inp-value=""
                :label="pField.title"
                :placeholder="pField.title"
              />
            </div>
          </div>
          </Transition>

          <Transition name="fade">
          <div v-show="isPaymentChecked">
            <div class="flex gap-4">
              <div class="flex items-center gap-2">
                <span class="flex justify-center items-center w-5 h-5 rounded-md border border-white
                  hover:border-neutral-100 transition-all cursor-pointer">
                  <input
                    value=""
                    :checked="allowPayLater"
                    id="aviable_p"
                    type="checkbox"
                    class="w-4 h-4 rounded-md text-neutral-100 border-neutral-100
                      focus:ring-0 focus:ring-offset-0 transition-all cursor-pointer"
                    @change="payLater($event as HtmlEvent)"
                  />
                </span>
                <label
                  for="aviable_p"
                  class="text-xs font-medium text-neutral-125 dark:text-gray-300 cursor-pointer"
                >
                  Allow users to book now and pay later
                </label>
              </div>

              <div class="flex items-center gap-2 cursor-pointer">
                <span class="flex justify-center items-center w-5 h-5 rounded-md border border-white
                  hover:border-neutral-100 transition-all cursor-pointer">
                  <input
                    value=""
                    :checked="allowPayFull"
                    id="aviable_full_pay"
                    type="checkbox"
                    class="w-4 h-4 rounded-md text-neutral-100 border-neutral-100
                      focus:ring-0 focus:ring-offset-0 transition-all cursor-pointer"
                    @change="payFull($event as HtmlEvent)"
                  />
                </span>
                <label
                  for="aviable_full_pay"
                  class="text-xs font-medium text-neutral-125 dark:text-gray-300 cursor-pointer"
                >
                  Allow users to 100% upfront regardless of the payment plan
                </label>
              </div>
            </div>
          </div>
          </Transition>

          <!-- Extras -->
          <article class="pt-4 border-t border-dashed border-neutral-50 transition-all ease-in-out duration-300">
            <CheckboxSlide
              :key="apiCallExist.extras.toString()"
              :isChecked="apiCallExist.extras"
              :disabled="apiCallExist.extras || isExtrasLoading"
              :tooltipTxt="apiCallExist.extras ? 'Extras already activated' : 'Extras not activated yet'"
              label="Activate extras"
              @update:isChecked="activateExtras"
            />
            <div
              v-show="apiCallExist.extras && !isExtrasLoading"
              class="max-w-[300px] pt-4"
            >
              <CheckboxSlide
                :isChecked="extras.enabled"
                :disabled="!apiCallExist.extras"
                label="Enable extras"
                @update:isChecked="enableExtras($event, 'enabled')"
              />
              <Transition name="fade">
                <CheckboxSlide
                  v-show="extras.enabled"
                  :isChecked="extras.is_grouped_by_category"
                  :disabled="!apiCallExist.extras"
                  class="pt-4 pl-14"
                  label="Group extras by category"
                  @update:isChecked="enableExtras($event, 'is_grouped_by_category')"
                />
              </Transition>
            </div>
            <div v-show="isExtrasNotValid" class="max-w-[700px] pt-2">
              <p v-html="extrasErrorMessage" class="text-sm" />
            </div>
          </article>

        </div>
        </Transition>

        <OverlaySpinner v-if="isExtrasLoading" />
      </div>
    </div>

    <!-- Terms & Conditions -->
    <div v-if="widget.config" class="tab-wrapper-primary mt-6">
      <div class="tab-heading-primary">{{ changeCase('terms and conditions', ElementE.TITLE) }}</div>
      <div class="p-6">
        <CheckboxSlide
          label="Use custom terms and conditions"
          :isChecked="widget.config?.custom_terms_enabled ?? false"
          @update:isChecked="toggleCustomTerms"
        />
        <div class="pt-3">
          <p
            v-if="!widget.config?.custom_terms_enabled"
            class="text-xs text-neutral-100"
          >
            Default terms and conditions are pulled from ResRequest.
          </p>
          <div v-else>
            <div class="flex items-center justify-between mb-1">
              <label class="label-primary" for="custom-terms">
                {{ changeCase('custom terms and conditions', ElementE.LABEL) }}
              </label>
              <button
                type="button"
                class="text-xs text-red-600 hover:text-red-800 font-medium"
                @click="confirmResetTerms"
              >
                Reset to default
              </button>
            </div>
            <p v-if="isTermsLoading" class="text-xs text-neutral-100 py-4">Loading default terms...</p>
            <textarea
              v-else
              id="custom-terms"
              v-model="customTermsContent"
              class="input-primary block h-40"
              :placeholder="(changeCase('enter your custom terms and conditions...', ElementE.PLACEHOLDER) as string)"
              @input="setCustomTermsContent"
            />
            <p class="mt-1 text-xs text-neutral-100">
              HTML is supported for formatting (e.g. &lt;b&gt;bold&lt;/b&gt;, &lt;ul&gt;&lt;li&gt;lists&lt;/li&gt;&lt;/ul&gt;, &lt;a href="..."&gt;links&lt;/a&gt;).
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import axiosClient from "@/axiosClient";

import NotificationPopup from "../../components/notifications/NotificationPopup.vue";
import PaymentDropdown from "@/components/inputs/PaymentDropdown.vue";
import OverlaySpinner from "@/components/loader/OverlaySpinner.vue";

import {
  FilledObjectI,
  PaymentFieldsI,
  PaymentMethodsI,
  PaymentResultObject,
  WidgetI,
} from "@/interfaces/interfaces";

import CheckboxSlide from "../inputs/CheckboxSlide.vue";
import BaseTooltip from "../notifications/BaseTooltip.vue";
import QuestionMarkIcon from "../icons/QuestionMarkIcon.vue";
import PaymentTextInput from "../inputs/PaymentTextInput.vue";
import ErrorIcon from "../icons/ErrorIcon.vue";
import TrashCanIcon from "../icons/TrashCanIcon.vue";
import { WIDGET_FIELD_MAX_LENGTH, WIDGET_DESCRIPTION_MAX_LENGTH } from '../../constants'
import { changeCase, validatePaymentConfig } from "../../helpers";
import {
  ElementE,
  EmailFieldsE,
} from '../../enums'

interface HtmlEvent extends Event {
  target: HTMLInputElement;
}

interface ErrorI {
  widgetName: string;
  widgetDescription: string;
  contactEmail: string;
  ccEmails: string;
  originatingEmail: string;
  personName: string;
  additionalEmailLinksHeading: string;
  [key: string]: string;
}

interface EmailLinkI {
  title: string;
  url: string;
}

interface EmailLinkWithErrorsI {
  id: string;
  title: string;
  url: string;
  description?: string;
  errors?: {
    title?: string;
    url?: string;
    description?: string;
  };
}

export default defineComponent({
  name: "GeneralTab",
  emits: ["updateWidget"],
  components: {
    CheckboxSlide,
    BaseTooltip,
    NotificationPopup,
    PaymentTextInput,
    ErrorIcon,
    PaymentDropdown,
    QuestionMarkIcon,
    OverlaySpinner,
    TrashCanIcon,
  },
  props: [
    "widgets",
    "paymentProp",
    "saveLabel",
    "isPropertiesUpdated",
  ],
  data() {
    return {
      isShowPaymentError: false,
      allowPayFull: true,
      allowPayLater: true,
      isSimplePaymentField: false,
      paymentMethod: "Choose payment method" as string,
      email: "" as string,
      additionalEmail: "" as string | null,
      name: "" as string | null,
      isBookingChecked: false as boolean,
      isPaymentChecked: false as boolean,
      widget: {} as WidgetI,
      payment_gateway_config: {},
      payments: {} as PaymentMethodsI,
      paymentFields: [] as PaymentFieldsI[],
      updatedPaymentFields: [] as PaymentFieldsI[],
      contactEmailFieldValue: "",
      ccEmailsFieldValue: "",
      originatingEmailFieldValue: "",
      personNameFieldValue: "",
      errors: {
        widgetName: "",
        widgetDescription: "",
        contactEmail: "",
        ccEmails: "",
        originatingEmail: "",
        personName: "",
        additionalEmailLinksHeading: "",
      } as ErrorI,
      changeCase: changeCase,
      ElementE: ElementE,
      EmailFieldsE,
      specials: {
        enabled: false,
        between_available: true,
        above_matrix: true,
        near_search: true,
        only_one_left: false,
      },
      supportEmail: 'support@resrequest.com',
      isSpecialsLoading: false,
      apiCallExist: {
        specials: false,
        extras: false,
      },
      isSpecialsNotValid: false,
      specialsErrorMessage: 'To activate Specials, select at least one property from the Properties tab, then Save and Publish. Please note that your ResRequest system must be on version 8.2 or higher for this feature to work.',
      extras: {
        enabled: false,
        is_grouped_by_category: false,
      },
      widgetContainer: null as HTMLElement | null,
      isExtrasLoading: false,
      isExtrasNotValid: false,
      extrasErrorMessage: 'To activate Extras, please note that your ResRequest system must be on version 8.2 or higher for this feature to work.',
      additionalEmailLinks: [] as EmailLinkWithErrorsI[],
      additionalEmailLinksHeading: "",
      additionalEmailLinksLayout: "inline",
      customTermsContent: "" as string,
      isTermsLoading: false,
      defaultTermsCache: null as string | null,
    };
  },
  computed: {
    errorTextWithSupportLink() {
      return `Credit card payments aren't enabled due to an incorrect payment gateway setup in ResRequest.
        Please check the setup or contact our <a href="mailto:${this.supportEmail}" class="underline hover:opacity-80">support team</a>.`;
    },
  },
  mounted() {
    this.widgetContainer = document.getElementById('editWidgetFlow');
    this.widget = this.widgets;
    this.payments = this.paymentProp;
    if(this.widgets.config != undefined &&
      this.widgets.config.payment_gateway_api != undefined &&
      this.widgets.config.payment_gateway_config?.length !== 0) {
      this.paymentMethod = this.widgets.config.payment_gateway_api;
    }

    // Check if Specials and Extras can be activated
    this.initAdditionalServices();
  },
  watch: {
    isPaymentChecked(value) {
      if (value) {
        if (this.widget.config.payment_gateway_api && this.widget.config.payment_gateway_config) {
          this.isShowPaymentError = !validatePaymentConfig(this.widget.config.payment_gateway_api, this.widget.config.payment_gateway_config);
        }
      }
    },

    isBookingChecked(booleanValue) {
      if (!booleanValue && !this.contactEmailFieldValue) {
        this.errors.contactEmail = "Email is required";
      }
    },

    paymentProp: function (paymentProp: PaymentMethodsI) {
      this.payments = paymentProp;
    },
    widgets: function (widgets: WidgetI) {
      this.widget = widgets;
      if (
        widgets.config !== undefined &&
        widgets.config.enable_bookings !== undefined
      ) {
        this.isBookingChecked = widgets.config.enable_bookings;
      }

      if (
        widgets.config !== undefined &&
        widgets.config.enable_card_payments !== undefined
      ) {
        this.isPaymentChecked = widgets.config.enable_card_payments;
      }

      if (this.widgets.config.contact_email != undefined) {
        this.contactEmailFieldValue = this.widgets.config.contact_email;
      }

      if (this.widget.config.name != undefined) {
        this.name = this.widget.config.name;
      } else {
        this.widget.config.name = "";
      }

      if (this.widgets.config.cc_emails != undefined) {
        this.ccEmailsFieldValue = this.widgets.config.cc_emails;
      }

      if (this.widgets.config.name != undefined) {
        this.personNameFieldValue = this.widgets.config.name;
      }

      if (this.widgets.config.originating_email != undefined) {
        this.originatingEmailFieldValue = this.widgets.config.originating_email;
      }

      if (this.widgets.config.payment_gateway_api != undefined
        && (this.widgets.config.payment_gateway_config === undefined
          || this.widgets.config.payment_gateway_config.length !== 0)) {
        this.paymentMethod = this.widgets.config.payment_gateway_api;
      }

      if (this.widgets.config.can_pay_full != undefined) {
        this.allowPayFull = this.widgets.config.can_pay_full;
      }

      if (this.widgets.config.can_pay_later != undefined) {
        this.allowPayLater = this.widgets.config.can_pay_later;
      }

      if (this.widget.config.payment_gateway_config != undefined && this.widgets.config.payment_gateway_config.length !== 0) {
        if (
          Object.keys(this.widget.config.payment_gateway_config).length > 0 &&
          this.widget.config.payment_gateway_config.constructor === Object
        ) {
          this.isSimplePaymentField = true;
        }
        this.payment_gateway_config = this.widget.config.payment_gateway_config;
        this.updatedPaymentFields = Object.keys(this.widget.config.payment_gateway_config).map(item => {return {field_name: item}});
        this.paymentFields = Object.keys(this.widget.config.payment_gateway_config).map(item => {return {field_name: item}});
      }

      // Check if Specials and Extras can be activated
      this.initAdditionalServices();

      if (widgets.config.specials) {
        this.specials = widgets.config.specials;
      }

      if (this.widgets.config?.extras) {
        this.extras = this.widgets.config.extras;
      }

      // Load additional email links
      if ((this.widgets.config as any)?.additional_email_links) {
        this.additionalEmailLinks = (this.widgets.config as any).additional_email_links.map((link: EmailLinkI, index: number) => ({
          id: `${Date.now()}-${index}`,
          title: link.title || '',
          url: link.url || '',
          description: (link as any).description || '',
          errors: {}
        }));
      }

      // Load additional email links heading
      if ((this.widgets.config as any)?.additional_email_links_heading) {
        this.additionalEmailLinksHeading = (this.widgets.config as any).additional_email_links_heading;
      }

      // Load additional email links layout
      if ((this.widgets.config as any)?.additional_email_links_layout) {
        this.additionalEmailLinksLayout = (this.widgets.config as any).additional_email_links_layout;
      }

      // Load custom terms content
      if (this.widgets.custom_terms_content != undefined) {
        this.customTermsContent = this.widgets.custom_terms_content;
      }
    },
    'specials.enabled': {
      handler(value: boolean) {
        this.specials.above_matrix = value;
        this.specials.between_available = value;
        this.specials.near_search = value;
      }
    },
    async isPropertiesUpdated(value: boolean) {
      if(value) {
        // Check if Specials and Extras can be activated
        await this.initAdditionalServices(true);
        // Force update widget with new Specials and Extras state
        this.saveWidget();
      }
    },
  },
  methods: {
    async fetchDefaultTerms(): Promise<string> {
      if (this.defaultTermsCache !== null) return this.defaultTermsCache;
      try {
        this.isTermsLoading = true;
        const widgetId = this.$route.params.wid;
        const { data } = await axiosClient.get(`v4/widget/${widgetId}/default-terms`);
        this.defaultTermsCache = data.terms || '';
        return this.defaultTermsCache as string;
      } catch {
        return '';
      } finally {
        this.isTermsLoading = false;
      }
    },
    async toggleCustomTerms(value: boolean) {
      this.widget.config.custom_terms_enabled = value;
      if (value && !this.customTermsContent) {
        const defaultTerms = await this.fetchDefaultTerms();
        this.customTermsContent = defaultTerms;
        this.widget.custom_terms_content = defaultTerms;
      }
      this.$emit('updateWidget', this.widget);
    },
    async confirmResetTerms() {
      if (!confirm('Reset to the default terms and conditions from ResRequest? Your custom text will be replaced.')) return;
      const defaultTerms = await this.fetchDefaultTerms();
      this.customTermsContent = defaultTerms;
      this.widget.custom_terms_content = defaultTerms;
      this.$emit('updateWidget', this.widget);
    },
    setCustomTermsContent() {
      this.widget.custom_terms_content = this.customTermsContent;
      this.$emit('updateWidget', this.widget);
    },
    validateField(fieldName: string, value: string) {
      if (fieldName === "widgetName" && !value) {
        this.errors[fieldName] = "The field should not be empty";
        return;
      }

      if (fieldName === "widgetDescription") {
        if (value && value.length > WIDGET_DESCRIPTION_MAX_LENGTH) {
          this.errors[
            fieldName
          ] = `The length of field must be less than ${WIDGET_DESCRIPTION_MAX_LENGTH} characters`;
          return;
        }
      } else {
        if (value && value.length > WIDGET_FIELD_MAX_LENGTH) {
          this.errors[
            fieldName
          ] = `The length of field must be less than ${WIDGET_FIELD_MAX_LENGTH} characters`;
          return;
        }
      }

      this.errors[fieldName] = "";
    },

    validateEmail(fieldName: string, value: string) {
      const emailRegex = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
      const urlRegex = /^(https?:\/\/(www\.)?|www\.)?([a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+)(\/[^?\s]*)?(\?[^\s]*)?$/;


      if (fieldName === 'contactEmail') {
        if (!value) {
          this.errors[fieldName] = "The field should not be empty";
          return;
        }

        if (value && (!emailRegex.test(value) && !urlRegex.test(value))) {
          this.errors[fieldName] = "Should be a vaild email or url address";
          return;
        }
      } else if (fieldName === 'ccEmails') {
        if (!value) return;

        const emails = value.split(/[;,]/).map((email) => email.trim().toLocaleLowerCase());
        const isMultipleEmailsFieldValid = emails.every((m) => emailRegex.test(m));
        if (!isMultipleEmailsFieldValid) {
          this.errors[fieldName] = "Should be a vaild email address / addresses";
          return;
        }
      } else {
        if (value && !emailRegex.test(value)) {
          this.errors[fieldName] = "Should be a vaild email address";
          return;
        }
      }

      this.errors[fieldName] = "";
    },

    payFull(event: HtmlEvent) {
      this.allowPayFull = event.target.checked;
      this.widget.config.can_pay_full = event.target.checked;
      this.updateWidget();
    },
    payLater(event: HtmlEvent) {
      this.allowPayLater = event.target.checked;
      this.widget.config.can_pay_later = event.target.checked;
      this.updateWidget();
    },

    saveWidgetName(inputValue: string) {
      this.validateField('widgetName', inputValue);

      if (this.widget && this.widget.config) {
        this.widget.name = inputValue;
        this.updateWidget();
      }
    },

    saveWidgetDescription(inputValue: string) {
      this.validateField('widgetDescription', inputValue);

      if (this.widget && this.widget.config) {
        this.widget.description = inputValue;
        this.updateWidget();
      }
    },

    saveContactEmail: function (inputValue: string) {
      if (this.widget && this.widget.config) {
        this.widget.config.contact_email = inputValue;
        this.updateWidget();
      }
    },

    saveCcEmails(inputValue: string) {
      if (!this.widget?.config) return;
      this.widget.config.cc_emails = inputValue;
      this.updateWidget();
    },

    saveOriginatingEmail: function (inputValue: string) {
      if (this.widget && this.widget.config) {
        this.widget.config.originating_email = inputValue;
        this.updateWidget();
      }
    },

    savePField: function (inputValue: string) {
      if (this.widget && this.widget.config) {
        this.widget.config.originating_email = inputValue;
        this.updateWidget();
      }
    },

    saveName: function (inputValue: string) {
      if (this.widget && this.widget.config) {
        this.widget.config.name = inputValue;
        this.updateWidget();
      }
    },

    updateWidget() {
      this.$emit("updateWidget", this.widget);
    },

    paymentEnable(event: HtmlEvent) {
      if (event.target.checked === true) {
        this.isShowPaymentError = true;
        if (
          this.widget.config !== undefined &&
          this.widget.config.enable_card_payments != undefined
        ) {
          this.widget.config.enable_card_payments = true;
          this.isPaymentChecked = true;
        }
      } else if (event.target.checked === false) {
        this.isShowPaymentError = false;
        this.allowPayFull = true;
        this.allowPayLater = true;
        if (
          this.widget.config !== undefined &&
          this.widget.config.enable_card_payments != undefined
        ) {
          this.widget.config.enable_card_payments = false;
          this.isPaymentChecked = false;
          this.widget.config.can_pay_full = true;
          this.widget.config.can_pay_later = true;
        }
      }

      this.updateWidget();
    },

    bookingEnable(event: HtmlEvent) {
      if (event.target.checked === true) {
        setTimeout(() => {
          (this.$refs.bookingAndPayments as HTMLElement).scrollIntoView({ behavior: 'smooth' });
        }, 0);

        if (
          this.widget.config !== undefined &&
          this.widget.config.enable_bookings != undefined
        ) {
          this.widget.config.enable_bookings = true;
          this.isBookingChecked = true;
          this.widget.config.can_pay_full = true;
          this.widget.config.can_pay_later = true;
        }
      } else if (event.target.checked === false) {
        this.widgetContainer?.scrollIntoView({ behavior: 'smooth' });
        this.isBookingChecked = false;
        if (
          this.widget.config !== undefined &&
          this.widget.config.enable_bookings != undefined
        ) {
          this.widget.config.enable_bookings = false;
        }
      }

      this.updateWidget();
    },

    paymentSet(field_slug: FilledObjectI) {
      this.isSimplePaymentField = false;
      this.paymentMethod = field_slug.slug;
      this.widget.config.payment_gateway_api = field_slug.slug;
      this.paymentFields = JSON.parse(JSON.stringify(field_slug.payOptions));
      this.widget.config.payment_gateway_config = {};
      this.updatedPaymentFields = [];
      this.isShowPaymentError = true;
      this.updateWidget();
    },
    savePaymentField(payment: PaymentResultObject) {
      const arrayPaymentFields = JSON.parse(JSON.stringify(this.paymentFields))

      if(payment.val.length === 0) {
        this.updatedPaymentFields = this.updatedPaymentFields.filter((item) => (item.field_name !== payment.id))
      }

      if(!this.updatedPaymentFields.some((item) => (item.field_name === payment.id)) && payment.val.length !== 0) {
        const findPayment = arrayPaymentFields.find((item: { field_name: string; }) => (payment.id === item.field_name));
        this.updatedPaymentFields = [...this.updatedPaymentFields, findPayment];
      }

      this.isShowPaymentError = this.updatedPaymentFields.length !== arrayPaymentFields.length;

      Object.assign(this.widget.config.payment_gateway_config, {
        [payment.id]: payment.val,
      });

      this.updateWidget();
    },
    enableSpecials(value: boolean, key: string) {
      this.specials[key as keyof typeof this.specials] = value;
      this.widget.config.specials = this.specials;
      this.updateWidget();
    },
    enableExtras(value: boolean, key: string) {
      this.extras[key as keyof typeof this.extras] = value;
      this.widget.config.extras = this.extras;
      this.updateWidget();
    },
    async initAdditionalServices(isPropertyChanged = false) {
      if (this.widget.config?.apiCallExist?.specials) {
        this.apiCallExist.specials = this.widget.config?.apiCallExist?.specials;
      }
      if (this.widget.config?.apiCallExist?.extras) {
        this.apiCallExist.extras = this.widget.config?.apiCallExist?.extras;
      }

      try {
        this.isSpecialsLoading = true;
        this.isExtrasLoading = true;

        if (isPropertyChanged) {
          await this.getApiCall(this.widget.id);
        }

        if (this.widget.config?.specials) {
          this.specials = this.widget.config.specials;
        }

        if (!this.apiCallExist?.specials) {
          this.specials.enabled = false;
        }

        if (this.widget.config?.extras) {
          this.extras = this.widget.config.extras;
        }

        if (!this.apiCallExist?.extras) {
          this.extras.enabled = false;
        }
      } catch (error) {
        console.error('Error initializing:', error);
      } finally {
        this.isSpecialsLoading = false;
        this.isExtrasLoading = false;
      }
    },
    async activateSpecials(checked: boolean) {
      if (!this.widget.id) return;
      this.isSpecialsLoading = true;
      this.apiCallExist.specials = checked;

      try {
        await this.getApiCall(this.widget.id);
        this.widget.config = {
          ...this.widget.config,
          apiCallExist: {
            specials: this.apiCallExist.specials,
            extras: this.apiCallExist.extras,
          },
        };
        this.updateWidget();
      } catch (error) {
        console.error('Error activating specials:', error);
      } finally {
        this.isSpecialsLoading = false;
      }
    },
    async activateExtras(checked: boolean) {
      if (!this.widget.id) return;
      this.isExtrasLoading = true;
      this.apiCallExist.extras = checked;

      try {
        await this.getApiCall(this.widget.id);
        this.widget.config = {
          ...this.widget.config,
          apiCallExist: {
            specials: this.apiCallExist.specials,
            extras: this.apiCallExist.extras,
          },
        };
        this.updateWidget();
      } catch (error) {
        console.error('Error activating specials:', error);
      } finally {
        this.isExtrasLoading = false;
      }
    },
    async getApiCall(widgetId: string) {
      this.isSpecialsNotValid = false;
      this.isExtrasNotValid = false;

      try {
        const { data } = await axiosClient.get(`v4/api_calls/${widgetId}`);

        this.apiCallExist = data.api_call_exist;
        this.isSpecialsNotValid = !this.apiCallExist.specials;
        this.isExtrasNotValid = !this.apiCallExist.extras;
      } catch (error) {
        console.error('Error getting API call:', error);
        this.isSpecialsNotValid = true;
        this.isExtrasNotValid = true;
        this.apiCallExist = {
          specials: false,
          extras: false,
        };
      }
    },
    async saveWidget() {
      this.widget.config.specials = this.specials;
      this.widget.config.extras = this.extras;
      this.widget.config.apiCallExist = this.apiCallExist;

      try {
        await axiosClient.put("v3/widgets/" + this.$route.params.wid, this.widget);
      } catch (error) {
        console.error('Error saving widget:', error);
      }
    },
    addEmailLink() {
      this.additionalEmailLinks.push({ 
        id: Date.now().toString(), 
        title: '', 
        url: '',
        description: '',
        errors: {}
      });
    },

    saveEmailLink(index: number, field: 'title' | 'url' | 'description', value: string) {
      if (this.additionalEmailLinks[index]) {
        this.additionalEmailLinks[index][field] = value;
        this.saveAdditionalEmailLinksToWidget();
      }
    },

    validateEmailLink(index: number, field: 'title' | 'url' | 'description', value: string) {
      if (!this.additionalEmailLinks[index]) return;

      const emailRegex = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
      const urlRegex = /^(https?:\/\/(www\.)?|www\.)?([a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+)(\/[^?\s]*)?(\?[^\s]*)?$/;

      if (!this.additionalEmailLinks[index].errors) {
        this.additionalEmailLinks[index].errors = {};
      }

      if (field === 'title') {
        if (!value || value.trim() === '') {
          this.additionalEmailLinks[index].errors!.title = "Title is required";
        } else if (value.length > 100) {
          this.additionalEmailLinks[index].errors!.title = "Title must be less than 100 characters";
        } else {
          this.additionalEmailLinks[index].errors!.title = "";
        }
      } else if (field === 'url') {
        if (!value || value.trim() === '') {
          this.additionalEmailLinks[index].errors!.url = "Link is required";
        } else if (!emailRegex.test(value) && !urlRegex.test(value)) {
          this.additionalEmailLinks[index].errors!.url = "Should be a valid email or URL address";
        } else {
          this.additionalEmailLinks[index].errors!.url = "";
        }
      } else if (field === 'description') {
        if (value && value.length > 200) {
          this.additionalEmailLinks[index].errors!.description = "Description must be less than 200 characters";
        } else {
          this.additionalEmailLinks[index].errors!.description = "";
        }
      }
    },

    removeEmailLink(index: number) {
      this.additionalEmailLinks.splice(index, 1);
      this.saveAdditionalEmailLinksToWidget();
    },

    saveAdditionalEmailLinksHeading(inputValue: string) {
      if (this.widget && this.widget.config) {
        (this.widget.config as any).additional_email_links_heading = inputValue;
        this.updateWidget();
      }
    },

    saveAdditionalEmailLinksToWidget() {
      if (this.widget && this.widget.config) {
        (this.widget.config as any).additional_email_links = this.additionalEmailLinks.map(link => ({
          title: link.title,
          url: link.url,
          description: link.description || ''
        }));
        this.updateWidget();
      }
    },

    setAdditionalEmailLinksLayout(layout: string) {
      this.additionalEmailLinksLayout = layout;
      if (this.widget && this.widget.config) {
        (this.widget.config as any).additional_email_links_layout = layout;
        this.updateWidget();
      }
    },
  },
});
</script>

<style scoped>
.v-enter-active,
.v-leave-active {
  transition: opacity .5s ease-in-out;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .5s;
}

.fade-enter-from, .fade-leave-to  {
  opacity: 0;
}
</style>

